/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import StyledProps from '../../common/theme/AppThemeInterface';
import appTheme from '../../common/theme/appTheme.json';
import { SpaceVehicleListContainer } from './SpaceVehicleListContainer';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from '../../fleet/FleetMap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { openPlatformListDrawer } from '../../../store/ui/actions';
import { selectPlatformListDrawerOpen } from '../../../store';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export const PLATFORM_LIST_TAB_HEIGHT_PX = 48;

enum PlatformListTab {
  SPACE_VEHICLE = 0,
  NETWORK = 1,
}

const useStyles = makeStyles({
  root: {
    height: `${PLATFORM_LIST_TAB_HEIGHT_PX}px`,
    minHeight: `${PLATFORM_LIST_TAB_HEIGHT_PX}px`,
  },
});

export const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -1px;
  background-color: #465967;
  position: relative;
`;

export const StyledAppBar = styled(AppBar)`
  flexgrow: 1;
  background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
`;

export const StyledTabs = styled(Tabs)`
  .MuiTabs-indicator {
    background-color: #61ded8;
    height: 3px;
    z-index: 1;
  }
`;

const NetworkListPlaceholder = styled(Box)`
  padding: 16px;
  color: ${appTheme.primaryButtonTextColor};
`;

interface TransportMonitoringPlatformListContainerProps {
  isInFullVehicleView: boolean;
}

const TransportMonitoringPlatformListContainer: React.FC<TransportMonitoringPlatformListContainerProps> = ({ isInFullVehicleView }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState<PlatformListTab>(PlatformListTab.SPACE_VEHICLE);
  
  const platformListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);

  const handleTabChange = (_event: React.ChangeEvent, newTabIndex: PlatformListTab) => {
    setTabIndex(newTabIndex);
  };

  const tabLabels = ['Space Vehicles', 'Networks'];

  return (
    <React.Fragment>
      {platformListDrawerOpen ? (
        <Box position="absolute" width={KU_PLUS_KA_DRAWER_WIDTH_PX}>
          {/* Only show tabs in Transport Monitoring View (not Full Vehicle View) */}
          {!isInFullVehicleView && (
            <>
              <StyledAppBar data-test="transport_monitoring_platform_list_tab_container" elevation={0} position="static">
                <StyledTabs value={tabIndex} classes={{ root: classes.root }} onChange={handleTabChange}>
                  {tabLabels.map((tabLabel: string, index: number) => (
                    <Tab
                      data-test={`transport_monitoring_platform_list_tab_container__tab_${index}`}
                      key={tabLabel}
                      sx={{
                        minWidth: '50%',
                        height: `${PLATFORM_LIST_TAB_HEIGHT_PX}px`,
                        minHeight: `${PLATFORM_LIST_TAB_HEIGHT_PX}px`,
                        color: appTheme.primaryButtonTextColor,
                        opacity: 0.7,
                        lineHeight: '25px',
                        '&.Mui-selected': {
                          lineHeight: '25px',
                          color: appTheme.primaryButtonTextColor,
                          opacity: 1.0,
                        },
                      }}
                      label={tabLabel}
                    />
                  ))}
                </StyledTabs>
              </StyledAppBar>
              <Line />
            </>
          )}

          {/* Space Vehicle List Panel */}
          {tabIndex === PlatformListTab.SPACE_VEHICLE && <SpaceVehicleListContainer />}

          {/* Network List Panel - Placeholder */}
          {tabIndex === PlatformListTab.NETWORK && (
            <NetworkListPlaceholder>Network List - Coming Soon</NetworkListPlaceholder>
          )}
        </Box>
      ) : (
        <Box maxWidth="32px" position="absolute">
          <Button
            color="primary"
            data-test="open-platform-list-drawer-button"
            disableRipple
            sx={{
              minHeight: `calc(100vh - 108px)`,
              minWidth: '32px',
              maxWidth: '32px',
              alignItems: 'start',
              paddingTop: '22px',
              '&:hover': {
                backgroundColor: appTheme.buttonHoverRippleBackgroundColor,
              },
            }}
            onClick={() => {
              dispatch(openPlatformListDrawer());
            }}
          >
            <ChevronRightIcon sx={{ color: appTheme.primaryButtonTextColor }} fontSize="small" />
          </Button>
        </Box>
      )}
    </React.Fragment>
  );
};

export default TransportMonitoringPlatformListContainer;